import * as moment from 'moment';
import {Action, Reducer} from 'redux';

import {logger} from 'src/utils/logger';

import {API_HEADERS, CREDENTIALS} from 'src/portal/common/constants';

const log = logger.getLogger('portal/common');

export function handleApiResponse(response: Response) {
	const status = response.status;
	if (200 <= status && status < 300) {
		return response;
	} else if (status === 404) {
		return response;
	} else {
		throw new Error(`Request to ${response.url} failed with ${response.status} ${response.statusText}`);
	}
}

export function postJson(payload?: any): RequestInit {
	return {
		body: payload ? JSON.stringify(payload) : undefined,
		credentials: CREDENTIALS,
		headers: Object.assign({}, API_HEADERS, {
			'X-CSRFToken': getCookie('csrftoken'),
		}),

		method: 'POST',
	};
}
export function getJson(): RequestInit {
	return {
		credentials: CREDENTIALS,
		headers: Object.assign({}, API_HEADERS, {
			'X-CSRFToken': getCookie('csrftoken'),
		}),

		method: 'GET',
	};
}

/**
 * Read a value the server rendered with Django's `json_script` filter.
 */
export function readJsonScript(id: string): any {
	const el = document.getElementById(id);
	return el && el.textContent ? JSON.parse(el.textContent) : undefined;
}

/**
 * Given a cookie key `name`, returns the value of
 * the cookie or `''`, if the key is not found.
 *
 * Source: https://gist.github.com/hunan-rostomyan/28e8702c1cecff41f7fe64345b76f2ca
 */
function getCookie(name: string): string {
	const nameLenPlus = name.length + 1;
	return (
		document.cookie
			.split(';')
			.map((c) => c.trim())
			.filter((cookie) => {
				return cookie.substring(0, nameLenPlus) === `${name}=`;
			})
			.map((cookie) => {
				return decodeURIComponent(cookie.substring(nameLenPlus));
			})[0] || ''
	);
}

const ISO_8601 = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}.\d{3})Z$/;
const SHORT_DATE = /^(\d{4}-\d{2}-\d{2})$/;
const DATETIME = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;

/**
 * Recursively iterates over an object/array and parses all date-like strings into Moment instances.
 * @param root
 * @returns {any}
 */
export function parseDatesToMoment(root: any): any {
	if (root === null) {
		return null;
	} else if (root === undefined) {
		return undefined;
	} else if (Array.isArray(root)) {
		return root.map((x) => parseDatesToMoment(x));
	} else if (typeof root === 'object') {
		for (const k of Object.keys(root)) {
			root[k] = parseDatesToMoment(root[k]);
		}
		return root;
	} else if (typeof root === 'string' && (ISO_8601.test(root) || SHORT_DATE.test(root) || DATETIME.test(root))) {
		log.debug(`Parsing a date to Moment: ${root}`);
		return moment.utc(root);
	} else {
		return root;
	}
}
export const skuPartComparator = (a: {code: string}, b: {code: string}) => {
	return a.code === b.code ? 0 : a.code < b.code ? -1 : 1;
};

/**
 * Utility to chain multiple reducers after each other, each receiving the same action and the returned state of the previous one.
 * @param reducers: Any number of reducers, ideally with mutually disjoint actions.
 */
export function chainReducers<StateT, ActionT extends Action>(...reducers: Reducer<StateT, ActionT>[]) {
	return (state: StateT, action: ActionT) => reducers.reduce((state, reducer) => reducer(state, action), state);
}

/**
 * Converts a string (typically the local part of an email address) into a
 * value that satisfies the backend video-address regex ``^[a-z][a-z0-9_.-]+$``.
 *
 * - Lowercases everything.
 * - Replaces unsupported characters with ``-``.
 * - Collapses consecutive ``-`` into one.
 * - Strips leading/trailing ``-``.
 * - If the result doesn't start with a letter, prepends ``u``.
 */
export function slugifyVideoUsername(input: string): string {
	let slug = input
		.toLowerCase()
		.replace(/[^a-z0-9_.-]/g, '-')
		.replace(/-{2,}/g, '-')
		.replace(/^-+|-+$/g, '');

	if (slug && !/^[a-z]/.test(slug)) {
		slug = 'u' + slug;
	}

	return slug;
}
